/**
 * Subscription Management Page
 * Displays current plan, available plans, usage metrics, and payment history
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { applyPromoCode, type PromoCodeResult } from '../../services/promoCodeService';
import {
  useGetCurrentSubscription,
  useGetAvailablePlans,
  useGetUsage,
  useGetPaymentHistory,
  useChangePlan,
} from '../../hooks/useSubscription';
import { useAuthStore } from '../../store/authStore';
import { format } from 'date-fns';
import type { SubscriptionPlan, ChangePlanData } from '../../types';

// Reusable components
const Card = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-white shadow rounded-lg ${className}`}>{children}</div>
);

const CardHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="px-6 py-4 border-b border-gray-200">{children}</div>
);

const CardTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-lg font-semibold text-gray-900">{children}</h2>
);

const CardContent = ({ children }: { children: React.ReactNode }) => (
  <div className="px-6 py-4">{children}</div>
);

const Badge = ({ children, variant = 'default' }: { children: React.ReactNode; variant?: 'default' | 'success' | 'warning' | 'danger' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    danger: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]}`}>
      {children}
    </span>
  );
};

const ProgressBar = ({ percentage, variant = 'blue' }: { percentage: number; variant?: 'blue' | 'green' | 'yellow' | 'red' }) => {
  const variants = {
    blue: 'bg-blue-600',
    green: 'bg-green-600',
    yellow: 'bg-yellow-600',
    red: 'bg-red-600',
  };

  const getVariant = () => {
    if (percentage >= 90) return 'red';
    if (percentage >= 75) return 'yellow';
    return 'blue';
  };

  const actualVariant = variant || getVariant();

  return (
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div
        className={`h-2.5 rounded-full ${variants[actualVariant]}`}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
    </div>
  );
};

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full z-10">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">{title}</h3>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function SubscriptionManagementPage() {
  const { t } = useTranslation('subscriptions');
  const { user } = useAuthStore();
  const { data: currentSubscriptionData, isLoading: subscriptionLoading } = useGetCurrentSubscription();
  const { data: availablePlansData, isLoading: plansLoading } = useGetAvailablePlans();
  const { data: usageData, isLoading: usageLoading } = useGetUsage();
  const { data: paymentsData, isLoading: paymentsLoading } = useGetPaymentHistory();
  const changePlan = useChangePlan();

  const [changePlanModalOpen, setChangePlanModalOpen] = useState(false);
  const [cryptoPaymentModalOpen, setCryptoPaymentModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState<PromoCodeResult | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);

  const formatLimit = (value: number) => (value === -1 ? '∞' : value.toLocaleString());

  const currentSubscription = currentSubscriptionData?.subscription;
  const availablePlans = availablePlansData?.plans || [];
  const usage = usageData?.usage;
  const payments = paymentsData?.payments || [];

  // Check if current user is the account owner (only owners can change plans)
  const isOwner = user?.account?.owner_user_id === user?.id;

  // Check if user has paid plan
  const hasPaidPlan = user?.account?.can_use_platform ?? false;

  // Check if user is on free trial
  const isOnFreeTrial = currentSubscription?.plan?.slug === 'free-trial';

  // Calculate days remaining in trial
  const trialDaysRemaining = currentSubscription?.ends_at
    ? Math.max(0, Math.ceil((new Date(currentSubscription.ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  const handleChangePlanClick = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setChangePlanModalOpen(true);
  };

  const handleCryptoPaymentClick = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setPromoCode('');
    setPromoResult(null);
    setPromoError(null);
    setCryptoPaymentModalOpen(true);
  };

  const handleCloseCryptoModal = () => {
    setCryptoPaymentModalOpen(false);
    setSelectedPlan(null);
    setPromoCode('');
    setPromoResult(null);
    setPromoError(null);
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim() || !selectedPlan) return;
    setPromoLoading(true);
    setPromoError(null);
    setPromoResult(null);
    try {
      const result = await applyPromoCode(promoCode.trim(), selectedPlan.id);
      setPromoResult(result);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string; message?: string } } };
      const errorCode = axiosErr?.response?.data?.error;
      if (errorCode === 'already_used') {
        setPromoError(t('promo_code.error_already_used'));
      } else {
        setPromoError(t('promo_code.error_invalid'));
      }
    } finally {
      setPromoLoading(false);
    }
  };

  const handleConfirmPlanChange = () => {
    if (!selectedPlan) return;

    const data: ChangePlanData = { plan_id: selectedPlan.id };
    changePlan.mutate(data, {
      onSuccess: () => {
        setChangePlanModalOpen(false);
        setSelectedPlan(null);
      },
    });
  };

  if (subscriptionLoading || plansLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">{t('page.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('page.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('page.subtitle')}
        </p>
      </div>

      <div className="space-y-6">
        {/* Free Trial Banner */}
        {isOnFreeTrial && (
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🎉</span>
                  <h2 className="text-xl font-bold">{t('free_trial.banner_title')}</h2>
                </div>
                <p className="text-indigo-100 mb-3">
                  You have <span className="font-bold text-white">{trialDaysRemaining} days</span> remaining in your 14-day free trial.
                  Upgrade now to unlock more features and keep your campaigns running!
                </p>
                <div className="flex items-center gap-2 text-sm text-indigo-100">
                  <span>✓ {t('free_trial.feature_campaigns')}</span>
                  <span>•</span>
                  <span>✓ {t('free_trial.feature_domains')}</span>
                  <span>•</span>
                  <span>✓ {t('free_trial.feature_requests')}</span>
                </div>
              </div>
              {trialDaysRemaining <= 3 && (
                <div className="ml-4">
                  <button
                    onClick={() => {
                      const starterPlan = availablePlans.find(p => p.slug === 'starter');
                      if (starterPlan) handleCryptoPaymentClick(starterPlan);
                    }}
                    className="px-6 py-3 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-indigo-50 transition-colors"
                  >
                    {t('free_trial.upgrade_button')}
                  </button>
                </div>
              )}
            </div>
            {trialDaysRemaining <= 3 && (
              <div className="mt-4 pt-4 border-t border-indigo-400">
                <p className="text-sm text-indigo-100">
                  ⚠️ Your trial expires soon! Upgrade to avoid service interruption.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Current Plan - Only show if user has paid */}
        {hasPaidPlan && currentSubscription?.plan && !isOnFreeTrial && (
          <Card>
            <CardHeader>
              <CardTitle>{t('current_plan.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              {currentSubscription?.plan ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {currentSubscription.plan.name}
                    </h3>
                    {currentSubscription.plan.description && (
                      <p className="text-sm text-gray-500 mt-1">
                        {t(`plan_descriptions.${currentSubscription.plan.slug}`, { defaultValue: currentSubscription.plan.description })}
                      </p>
                    )}
                  </div>
                  <Badge variant={currentSubscription.is_active ? 'success' : 'danger'}>
                    {currentSubscription.is_active ? t('current_plan.status_active') : t('current_plan.status_inactive')}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <div className="text-sm text-gray-500">{t('current_plan.price')}</div>
                    <div className="text-lg font-semibold text-gray-900">
                      ${currentSubscription.plan.price}
                      {currentSubscription.plan.billing_period === 'monthly' ? t('current_plan.per_month') : t('current_plan.per_year')}
                    </div>
                  </div>

                  {currentSubscription.starts_at && (
                    <div>
                      <div className="text-sm text-gray-500">{t('current_plan.starts_at')}</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {format(new Date(currentSubscription.starts_at), 'MMM d, yyyy')}
                      </div>
                    </div>
                  )}

                  {currentSubscription.ends_at && (
                    <div>
                      <div className="text-sm text-gray-500">{t('current_plan.ends_at')}</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {format(new Date(currentSubscription.ends_at), 'MMM d, yyyy')}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">{t('plans.features')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-sm text-gray-500">{t('usage.campaigns')}</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {formatLimit(currentSubscription.plan.limits.campaigns)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">{t('features.domains_per_campaign')}</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {currentSubscription.plan.domains_per_campaign}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">{t('usage.requests')}</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {formatLimit(currentSubscription.plan.limits.requests_per_month)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">{t('usage.team_members')}</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {formatLimit(currentSubscription.plan.limits.team_members)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              ) : (
                <div className="text-gray-500">{t('payment_history.no_payments')}</div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Usage Metrics - Only show if user has paid */}
        {hasPaidPlan && usage && (
          <Card>
            <CardHeader>
              <CardTitle>{t('usage.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="text-sm text-gray-500 mb-4">
                  {t('usage.period')}: {format(new Date(usage.period.start), 'MMM d')} -{' '}
                  {format(new Date(usage.period.end), 'MMM d, yyyy')}
                </div>

                {/* Requests */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-700">{t('usage.requests')}</div>
                    <div className="text-sm text-gray-500">
                      {usage.current_period.requests.toLocaleString()} /{' '}
                      {usage.limits.requests_per_month.toLocaleString()}{' '}
                      <span className="font-medium">({usage.percentage.requests}%)</span>
                    </div>
                  </div>
                  <ProgressBar percentage={usage.percentage.requests} />
                </div>

                {/* Campaigns */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-700">{t('usage.campaigns')}</div>
                    <div className="text-sm text-gray-500">
                      {usage.current_period.campaigns} / {formatLimit(usage.limits.campaigns)}{' '}
                      {usage.limits.campaigns !== -1 && <span className="font-medium">({usage.percentage.campaigns}%)</span>}
                    </div>
                  </div>
                  {usage.limits.campaigns !== -1 && <ProgressBar percentage={usage.percentage.campaigns} />}
                </div>

                {/* Team Members */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-700">{t('usage.team_members')}</div>
                    <div className="text-sm text-gray-500">
                      {usage.current_period.team_members} / {formatLimit(usage.limits.team_members)}{' '}
                      {usage.limits.team_members !== -1 && <span className="font-medium">({usage.percentage.team_members}%)</span>}
                    </div>
                  </div>
                  {usage.limits.team_members !== -1 && <ProgressBar percentage={usage.percentage.team_members} />}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Available Plans */}
        {isOwner && (
          <Card>
            <CardHeader>
              <CardTitle>{t('plans.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {availablePlans.filter(plan => plan.slug !== 'free-trial').map((plan) => (
                  <div
                    key={plan.id}
                    className={`border rounded-lg p-6 ${
                      currentSubscription?.plan?.id === plan.id
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="text-center">
                      <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                      {plan.description && (
                        <p className="text-sm text-gray-500 mt-1">{t(`plan_descriptions.${plan.slug}`, { defaultValue: plan.description })}</p>
                      )}
                      <div className="mt-4">
                        <span className="text-3xl font-bold text-gray-900">${plan.price}</span>
                        <span className="text-gray-500">{plan.billing_period === 'monthly' ? t('current_plan.per_month') : t('current_plan.per_year')}</span>
                      </div>
                    </div>

                    <div className="mt-6 space-y-3">
                      <div className="flex items-center text-sm">
                        <svg
                          className="h-5 w-5 text-green-500 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>{t('plans.feature_campaigns', { count: formatLimit(plan.limits.campaigns) })}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <svg
                          className="h-5 w-5 text-green-500 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>{plan.domains_per_campaign} domains per campaign</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <svg
                          className="h-5 w-5 text-green-500 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>{t('plans.feature_requests', { count: plan.limits.requests_per_month.toLocaleString() })}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <svg
                          className="h-5 w-5 text-green-500 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>{t('plans.feature_team_members', { count: formatLimit(plan.limits.team_members) })}</span>
                      </div>
                    </div>

                    <div className="mt-6">
                      <button
                        onClick={() => handleCryptoPaymentClick(plan)}
                        className="btn btn-primary w-full"
                        type="button"
                      >
                        {t('plans.buy_with_crypto')}
                      </button>
                      {currentSubscription?.plan?.id === plan.id && (
                        <div className="mt-2 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {t('plans.current_badge')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment History */}
        <Card>
          <CardHeader>
            <CardTitle>{t('payment_history.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {paymentsLoading ? (
              <div className="text-gray-500">{t('payment_history.loading')}</div>
            ) : payments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('payment_history.date')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('payment_history.plan')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('payment_history.amount')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('payment_history.method')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('payment_history.status')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {payment.paid_at
                            ? format(new Date(payment.paid_at), 'MMM d, yyyy')
                            : format(new Date(payment.created_at), 'MMM d, yyyy')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {payment.subscription_plan?.name || t('payment_history.not_available')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${payment.amount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {payment.payment_method}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge
                            variant={
                              payment.status === 'completed'
                                ? 'success'
                                : payment.status === 'failed'
                                ? 'danger'
                                : 'warning'
                            }
                          >
                            {payment.status === 'completed'
                              ? t('payment_history.status_completed')
                              : payment.status === 'failed'
                              ? t('payment_history.status_failed')
                              : payment.status === 'pending'
                              ? t('payment_history.status_pending')
                              : t('payment_history.status_refunded')}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-gray-500">{t('payment_history.no_payments')}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Change Plan Confirmation Modal */}
      <Modal
        isOpen={changePlanModalOpen}
        onClose={() => {
          setChangePlanModalOpen(false);
          setSelectedPlan(null);
        }}
        title={t('change_plan_modal.title')}
      >
        {selectedPlan && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              {t('change_plan_modal.confirmation_message', { planName: selectedPlan.name })}
            </p>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-700">
                <div className="flex justify-between mb-2">
                  <span>{t('change_plan_modal.new_plan')}:</span>
                  <span className="font-semibold">{selectedPlan.name}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span>{t('change_plan_modal.price')}:</span>
                  <span className="font-semibold">
                    ${selectedPlan.price}
                    {selectedPlan.billing_period === 'monthly' ? t('current_plan.per_month') : t('current_plan.per_year')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t('change_plan_modal.effective_date')}:</span>
                  <span className="font-semibold">{t('change_plan_modal.effective_immediately')}</span>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              {t('change_plan_modal.note')}
            </p>

            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={() => {
                  setChangePlanModalOpen(false);
                  setSelectedPlan(null);
                }}
                disabled={changePlan.isPending}
                className="btn btn-secondary"
              >
                {t('change_plan_modal.cancel_button')}
              </button>
              <button
                type="button"
                onClick={handleConfirmPlanChange}
                disabled={changePlan.isPending}
                className="btn btn-primary"
              >
                {changePlan.isPending ? t('change_plan_modal.changing') : t('change_plan_modal.confirm_button')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Crypto Payment Modal */}
      <Modal
        isOpen={cryptoPaymentModalOpen}
        onClose={handleCloseCryptoModal}
        title={t('crypto_payment_modal.title')}
      >
        {selectedPlan && (
          <div className="space-y-6">
            {/* Plan Summary */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900">{selectedPlan.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{t(`plan_descriptions.${selectedPlan.slug}`, { defaultValue: selectedPlan.description })}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">${selectedPlan.price}</div>
                  <div className="text-sm text-gray-500">
                    {selectedPlan.billing_period === 'monthly' ? t('current_plan.per_month') : t('current_plan.per_year')}
                  </div>
                </div>
              </div>
            </div>

            {/* Promo Code Section */}
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium text-gray-700">{t('promo_code.label')}</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder={t('promo_code.placeholder')}
                  disabled={promoLoading || promoResult !== null}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleApplyPromo();
                  }}
                />
                <button
                  type="button"
                  onClick={handleApplyPromo}
                  disabled={promoLoading || !promoCode.trim() || promoResult !== null}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {promoLoading ? t('promo_code.applying') : t('promo_code.apply_button')}
                </button>
              </div>

              {/* Error state */}
              {promoError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-red-700">{promoError}</p>
                </div>
              )}

              {/* Success state */}
              {promoResult && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md space-y-2">
                  <p className="text-sm font-semibold text-green-700">
                    {t('promo_code.success', {
                      saved: promoResult.saved_amount.toFixed(2),
                      percent: promoResult.discount_percent,
                    })}
                  </p>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div className="flex justify-between">
                      <span>{t('promo_code.original_price')}:</span>
                      <span className="line-through text-gray-400">${promoResult.original_amount.toFixed(2)} USDT</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('promo_code.discount')} ({promoResult.discount_percent}%):</span>
                      <span className="text-red-500">-${promoResult.saved_amount.toFixed(2)} USDT</span>
                    </div>
                    <div className="flex justify-between font-semibold text-gray-900 border-t border-green-200 pt-1">
                      <span>{t('promo_code.final_price')}:</span>
                      <span className="text-green-700">${promoResult.discounted_amount.toFixed(2)} USDT</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Instructions */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('crypto_payment_modal.instructions_title')}</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                <li>{t('crypto_payment_modal.step_1')}</li>
                <li>{t('crypto_payment_modal.step_2')}</li>
                <li>{t('crypto_payment_modal.step_3')}</li>
              </ol>
            </div>

            {/* Payment Details */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="text-center mb-4">
                <h4 className="font-semibold text-gray-900 mb-1">{t('crypto_payment_modal.payment_details')}</h4>
                <p className="text-xs text-gray-500">{t('crypto_payment_modal.network')}: USDT (TRC20)</p>
              </div>

              {/* QR Code */}
              <div className="flex justify-center mb-4">
                <div className="bg-white border-2 border-gray-300 rounded-lg p-4">
                  <img
                    src="/payment-qr.jpeg"
                    alt={t('crypto_payment_modal.qr_code')}
                    className="w-48 h-48 object-contain"
                  />
                </div>
              </div>

              {/* Crypto Address */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('crypto_payment_modal.wallet_address')}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    readOnly
                    value="TCySzCCNkaPmXaw4RrGeSaUYVfGGSSTUBY"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-mono text-gray-900 pr-10"
                    onClick={(e) => {
                      e.currentTarget.select();
                      navigator.clipboard.writeText(e.currentTarget.value);
                      toast.success(t('messages.wallet_copied', 'Wallet address copied to clipboard!'));
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText('TCySzCCNkaPmXaw4RrGeSaUYVfGGSSTUBY');
                      toast.success(t('messages.wallet_copied', 'Wallet address copied to clipboard!'));
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title={t('crypto_payment_modal.copy')}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">{t('crypto_payment_modal.click_to_copy')}</p>
              </div>

              {/* Amount */}
              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('crypto_payment_modal.amount_to_send')}
                </label>
                <div className="bg-white px-3 py-2 border border-gray-300 rounded-md">
                  {promoResult ? (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-400 line-through">${selectedPlan.price} USDT</span>
                      <span className="text-lg font-bold text-green-600">${promoResult.discounted_amount.toFixed(2)} USDT</span>
                    </div>
                  ) : (
                    <div className="text-lg font-bold text-gray-900">${selectedPlan.price} USDT</div>
                  )}
                </div>
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="w-full">
                  <h4 className="font-semibold text-gray-900 mb-2">{t('crypto_payment_modal.important_notice')}</h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p className="flex items-start gap-2">
                      <span className="font-semibold text-gray-900 flex-shrink-0">1.</span>
                      <span>{t('crypto_payment_modal.step_after_payment')}</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="font-semibold text-gray-900 flex-shrink-0">2.</span>
                      <span>{t('crypto_payment_modal.approval_time')}</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="font-semibold text-gray-900 flex-shrink-0">3.</span>
                      <span>{t('crypto_payment_modal.confirmation_notice')}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Close Button */}
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleCloseCryptoModal}
                className="btn btn-primary px-8"
              >
                {t('crypto_payment_modal.close_button')}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
